import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Stack,
  Card,
  CardContent,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import DesignServicesIcon from '@mui/icons-material/DesignServices';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AttachFileIcon from '@mui/icons-material/AttachFile';
// 목록형 응답 공용 컴팩트 테이블 (BotResponseRenderer.js와 공유)
import { CompactListTable, renderTitleCell, renderStageCell } from './BotListTable';

/**
 * 제조 작업 완료 UI 컴포넌트
 * - 작업 유형 선택 (DESIGN, ROUTING, QC)
 * - 작업 목록 표시 및 선택
 * - 완료 확인 및 처리
 */
const ManufacturingTaskComplete = ({ data, onSendMessage }) => {
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);

  // 응답 유형에 따른 렌더링
  const responseType = data?.responseType;

  // 작업 유형 아이콘
  const getTaskTypeIcon = (type) => {
    switch (type) {
      case 'DESIGN':
        return <DesignServicesIcon sx={{ fontSize: 40, color: '#1976d2' }} />;
      case 'ROUTING':
        return <PrecisionManufacturingIcon sx={{ fontSize: 40, color: '#2e7d32' }} />;
      case 'QC':
        return <FactCheckIcon sx={{ fontSize: 40, color: '#ed6c02' }} />;
      default:
        return null;
    }
  };

  // 작업 유형명
  const getTaskTypeName = (type) => {
    switch (type) {
      case 'DESIGN': return '설계';
      case 'ROUTING': return '제조 공정';
      case 'QC': return '품질검사';
      default: return type;
    }
  };

  // 상태 색상
  const getStatusColor = (status) => {
    switch (status) {
      case 'PENDING': return 'warning';
      case 'IN_PROGRESS': return 'info';
      case 'COMPLETED': return 'success';
      case 'PASS': return 'success';
      case 'FAIL': return 'error';
      default: return 'default';
    }
  };

  // 작업 유형 선택 핸들러
  const handleTaskTypeSelect = (taskType) => {
    const fileUuid = data?.fileUuid || '';
    const fileName = data?.fileName || '';
    onSendMessage(`제조작업:${taskType}|${fileUuid}|${fileName}`);
  };

  // 작업 선택 핸들러
  const handleTaskSelect = (task) => {
    setSelectedTask(task);
    setConfirmOpen(true);
  };

  // 완료 확인 핸들러
  // result: QC 판정 결과('PASS'|'FAIL'). QC가 아닌 작업 유형은 무시된다.
  //
  // ★ 백엔드 지원 필요: 메시지 프로토콜(제조완료:type|taskId|fileUuid|fileName|confirmationToken)에
  // result 필드를 6번째로 덧붙여 보내지만, 현재 PmsBotService/ManufacturingBotService는
  // 이 필드를 읽지 않고 QC 완료를 항상 PASS로 하드코딩 처리한다(qcService.updateQcStatus(taskId, "PASS", ...)).
  // 백엔드가 5번째(index 5) result 필드를 파싱해 completeTask에 전달하고, PASS/FAIL에 따라
  // 분기하도록 고쳐야 불합격 처리가 실제로 동작한다. 그 전까지는 이 버튼을 눌러도 서버는 합격으로 처리한다.
  const handleConfirmComplete = (result = 'PASS') => {
    setLoading(true);
    const fileUuid = data?.fileUuid || '';
    const fileName = data?.fileName || '';
    const selectedTaskType = data?.selectedTaskType || '';
    const confirmationToken = data?.confirmationToken || '';
    const resultSuffix = selectedTaskType === 'QC' ? `|${result}` : '';
    onSendMessage(`제조완료:${selectedTaskType}|${selectedTask.taskId}|${fileUuid}|${fileName}|${confirmationToken}${resultSuffix}`);
    setConfirmOpen(false);
    setLoading(false);
  };

  // 뒤로 가기 (작업 유형 선택으로)
  const handleBack = () => {
    onSendMessage('제조 작업 선택');
  };

  // ============================================
  // 1. 작업 유형 선택 화면
  // ============================================
  if (responseType === 'MANUFACTURING_TASK_TYPE_SELECT') {
    const { designCount, routingCount, qcCount, fileName } = data;

    return (
      <Box sx={{ maxWidth: 400 }}>
        <Stack spacing={2}>
          {fileName && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <AttachFileIcon color="primary" />
              <Typography variant="body2" color="text.secondary">
                {fileName}
              </Typography>
            </Box>
          )}

          <Typography variant="subtitle1" fontWeight="bold">
            🔧 어떤 작업을 완료하시겠습니까?
          </Typography>

          <Stack spacing={1.5}>
            {/* 설계 완료 */}
            <Card 
              variant="outlined" 
              sx={{ 
                cursor: designCount > 0 ? 'pointer' : 'not-allowed',
                opacity: designCount > 0 ? 1 : 0.5,
                '&:hover': designCount > 0 ? { bgcolor: 'action.hover' } : {}
              }}
              onClick={() => designCount > 0 && handleTaskTypeSelect('DESIGN')}
            >
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  {getTaskTypeIcon('DESIGN')}
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="subtitle2">설계 완료</Typography>
                    <Typography variant="caption" color="text.secondary">
                      진행 중인 설계 작업 완료 처리
                    </Typography>
                  </Box>
                  <Chip 
                    label={`${designCount}건`} 
                    size="small" 
                    color={designCount > 0 ? 'primary' : 'default'}
                  />
                </Stack>
              </CardContent>
            </Card>

            {/* 제조 공정 완료 */}
            <Card 
              variant="outlined"
              sx={{ 
                cursor: routingCount > 0 ? 'pointer' : 'not-allowed',
                opacity: routingCount > 0 ? 1 : 0.5,
                '&:hover': routingCount > 0 ? { bgcolor: 'action.hover' } : {}
              }}
              onClick={() => routingCount > 0 && handleTaskTypeSelect('ROUTING')}
            >
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  {getTaskTypeIcon('ROUTING')}
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="subtitle2">제조 공정 완료</Typography>
                    <Typography variant="caption" color="text.secondary">
                      담당 공정 작업 완료 처리
                    </Typography>
                  </Box>
                  <Chip 
                    label={`${routingCount}건`} 
                    size="small"
                    color={routingCount > 0 ? 'success' : 'default'}
                  />
                </Stack>
              </CardContent>
            </Card>

            {/* 품질검사 완료 */}
            <Card 
              variant="outlined"
              sx={{ 
                cursor: qcCount > 0 ? 'pointer' : 'not-allowed',
                opacity: qcCount > 0 ? 1 : 0.5,
                '&:hover': qcCount > 0 ? { bgcolor: 'action.hover' } : {}
              }}
              onClick={() => qcCount > 0 && handleTaskTypeSelect('QC')}
            >
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  {getTaskTypeIcon('QC')}
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="subtitle2">품질검사 완료</Typography>
                    <Typography variant="caption" color="text.secondary">
                      QC 검사 완료(합격/불합격) 처리
                    </Typography>
                  </Box>
                  <Chip 
                    label={`${qcCount}건`} 
                    size="small"
                    color={qcCount > 0 ? 'warning' : 'default'}
                  />
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        </Stack>
      </Box>
    );
  }

  // ============================================
  // 2. 작업 목록 화면
  // ============================================
  if (responseType === 'MANUFACTURING_TASK_LIST') {
    const { tasks, selectedTaskType, selectedTaskTypeName, fileName } = data;

    return (
      <Box sx={{ maxWidth: 450 }}>
        <Stack spacing={2}>
          {/* 헤더 */}
          <Stack direction="row" alignItems="center" spacing={1}>
            <Button 
              size="small" 
              startIcon={<ArrowBackIcon />}
              onClick={handleBack}
            >
              뒤로
            </Button>
            <Divider orientation="vertical" flexItem />
            <Typography variant="subtitle1" fontWeight="bold">
              {getTaskTypeIcon(selectedTaskType)} {selectedTaskTypeName || getTaskTypeName(selectedTaskType)} 작업 선택
            </Typography>
          </Stack>

          {fileName && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AttachFileIcon fontSize="small" color="action" />
              <Typography variant="caption" color="text.secondary">
                첨부: {fileName}
              </Typography>
            </Box>
          )}

          <Typography variant="body2" color="text.secondary">
            완료할 작업을 선택해주세요. ({tasks?.length || 0}건)
          </Typography>

          {/* 작업 목록 - 컴팩트 테이블: 작업명(굵게)이 핵심, 생산계획번호/제품명/순서는 작게.
              상태는 배지로 오른쪽에 분리해 한눈에 보이게 한다. 행을 누르면 기존처럼 완료 확인
              다이얼로그가 열린다. */}
          <Box sx={{ maxHeight: 320, overflowY: 'auto' }}>
            <CompactListTable
              items={tasks || []}
              getRowKey={(task, index) => task.taskId || index}
              onRowClick={(task) => handleTaskSelect(task)}
              columns={[
                {
                  key: 'task',
                  header: '작업',
                  width: '58%',
                  render: (task) => renderTitleCell(
                    task.taskName,
                    [task.planNumber, task.productName, task.processOrder ? `${task.processOrder}순서` : null].filter(Boolean).join(' · '),
                    '작업명 없음'
                  ),
                },
                {
                  key: 'status',
                  header: '상태',
                  width: '42%',
                  render: (task) => renderStageCell(task.taskStatusName, getStatusColor(task.taskStatus), getStatusColor(task.taskStatus) === 'default' ? 'outlined' : 'filled'),
                },
              ]}
            />
          </Box>
        </Stack>

        {/* 완료 확인 다이얼로그 */}
        <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
          <DialogTitle>
            ✅ 작업 완료 확인
          </DialogTitle>
          <DialogContent>
            {selectedTask && (
              <Stack spacing={2} sx={{ mt: 1 }}>
                <Typography variant="body2">
                  {selectedTaskType === 'QC' ? '다음 작업을 합격/불합격 처리하시겠습니까?' : '다음 작업을 완료 처리하시겠습니까?'}
                </Typography>
                <Card variant="outlined">
                  <CardContent sx={{ py: 1.5 }}>
                    <Stack spacing={1}>
                      <Typography variant="subtitle2">
                        📋 {selectedTask.taskName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        🏭 {selectedTask.planNumber}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        📦 {selectedTask.productName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        📎 첨부: {fileName}
                      </Typography>
                    </Stack>
                  </CardContent>
                </Card>
                {selectedTaskType === 'QC' && (
                  <Alert severity="warning">
                    불합격 처리는 되돌릴 수 없습니다. 신중히 선택해주세요.
                  </Alert>
                )}
              </Stack>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmOpen(false)}>취소</Button>
            {selectedTaskType === 'QC' ? (
              <>
                <Button
                  variant="contained"
                  color="error"
                  onClick={() => handleConfirmComplete('FAIL')}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={16} /> : null}
                >
                  불합격 처리
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  onClick={() => handleConfirmComplete('PASS')}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={16} /> : <CheckCircleIcon />}
                >
                  합격 처리
                </Button>
              </>
            ) : (
              <Button
                variant="contained"
                onClick={() => handleConfirmComplete('PASS')}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={16} /> : <CheckCircleIcon />}
              >
                완료 처리
              </Button>
            )}
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  // ============================================
  // 3. 완료 성공 화면
  // ============================================
  if (responseType === 'MANUFACTURING_COMPLETE_SUCCESS') {
    const result = data;

    // 타이디 카드: 작업명(제목) + 완료 배지(오른쪽), 생산계획/제품/첨부는 작은 보조 텍스트,
    // 다음 안내는 강조 텍스트로 - 기존에 있던 모든 정보(제목 Alert 포함)를 한 카드로 정리했다.
    return (
      <Box sx={{ maxWidth: 420 }}>
        <Card variant="outlined">
          <CardContent>
            <Stack spacing={1}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                <Typography variant="subtitle1" fontWeight="bold">
                  {result.taskName || '작업'}
                </Typography>
                <Chip
                  icon={<CheckCircleIcon fontSize="small" />}
                  label={`${result.taskTypeName ? `${result.taskTypeName} ` : ''}완료`}
                  size="small"
                  color="success"
                />
              </Stack>
              <Typography variant="caption" color="text.secondary">
                {[
                  result.planNumber ? `생산계획 ${result.planNumber}` : null,
                  result.productName ? `제품 ${result.productName}` : null,
                  result.fileName ? `첨부 ${result.fileName}` : null,
                ].filter(Boolean).join(' · ')}
              </Typography>
              {(result.nextStep || result.nextStepMessage) && <Divider />}
              {result.nextStep && (
                <Typography variant="body2" color="primary" sx={{ fontWeight: 'medium' }}>
                  ➡️ {result.nextStep}
                </Typography>
              )}
              {result.nextStepMessage && (
                <Typography variant="caption" color="text.secondary">
                  {result.nextStepMessage}
                </Typography>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // ============================================
  // 4. 작업 없음 화면
  // ============================================
  if (responseType === 'MANUFACTURING_NO_TASK') {
    return (
      <Alert severity="info">
        <Typography variant="body2">
          {data?.message || '현재 진행 중인 제조 작업이 없습니다.'}
        </Typography>
      </Alert>
    );
  }

  // 기본 렌더링
  return null;
};

export default ManufacturingTaskComplete;
